import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { ACCOUNT_QUERY_KEYS } from '@/lib/constants/account';
import { AccountApi } from '../AccountApi';
import { AccountUtils } from '../AccountUtils';
import type { IInvoiceViewData } from '@/lib/types/Billing';

/** Fetches invoices for the parent account, sorted by invoice number descending. */
export function useInvoices() {
  const { authData, customerInfo } = useAuth();
  const parentCId = AccountUtils.resolveParentCId(customerInfo, authData);

  const query = useQuery({
    queryKey: ACCOUNT_QUERY_KEYS.invoices(parentCId ?? 0),
    queryFn: async () => {
      const result = await AccountApi.getInvoices(parentCId!);
      return result.data;
    },
    enabled: parentCId != null,
    staleTime: 5 * 60 * 1000,
  });

  const invoices: IInvoiceViewData[] = useMemo(() => {
    if (!query.data) return [];
    // Sort by invoice number descending (newest first)
    return [...query.data].sort((a, b) => b.invoiceNumber - a.invoiceNumber);
  }, [query.data]);

  return {
    invoices,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
}
